import { Component, Element, Host, Prop, Watch, h } from '@stencil/core';
import { isEnterKey, renderResponsiveImages } from '../../helpers/helpers';
import type {
  CssClassMap,
  Product,
  ResponsiveImages,
} from '../../helpers/utils/interfaces';
import type { CmsOverlay } from '../cms-overlay/cms-overlay';

@Component({
  tag: 'cms-showroom',
  styleUrl: 'cms-showroom.scss',
  shadow: true,
})
export class CmsShowroom {
  private responsiveImages: ResponsiveImages;
  private articles: Product[];
  private overlay: CmsOverlay;

  /**
   * Set  Layout full 100% or two-thirds 66%
   */
  @Prop({ mutable: true }) layout: 'full' | 'two-thirds';
  /**
   * set the responsive images for Breakpoints 320, 480, 768,992,1200
   */
  @Prop({ mutable: true }) images!: ResponsiveImages | string;
  /**
   * set the Article List
   */
  @Prop({ mutable: true }) products!: Product[] | string;
  /**
   * set the open Overlay Text
   */
  @Prop({ mutable: true }) openOverlayText: string;
  /**
   * set the overlay state
   */
  @Prop({ mutable: true }) open = false;
  /**
   * set the overlay headline
   */
  @Prop({ mutable: true }) overlayHeadline: string;
  /**
   * set the image alt text
   */
  @Prop({ mutable: true }) imageAltText = 'Hornbach';
  /**
   * Set the tracking namespace (optional)
   */
  @Prop({ mutable: true }) namespace?: string;
  /**
   * Set the title which is used in the ratings
   */
  @Prop({ mutable: true }) ratingTitle = '';
  /**
   * set accessible-name for the energy-label datasheet-link
   */
  @Prop({ mutable: true }) datasheetAccessibleName?: string;

  @Element() hostElement: HTMLElement;

  @Watch('images')
  private objectImagesWatcher(newValue: ResponsiveImages | string) {
    if (newValue) {
      this.responsiveImages =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
    }
  }

  @Watch('products')
  private arrayProductsWatcher(newValue: Product[] | string) {
    if (newValue) {
      this.articles =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
    }
  }

  private responsiveImagesSizes() {
    if (this.layout === 'full') {
      return `
    (max-width: 1279px) and (min-resolution: 288dpi) 33vw,
    (max-width: 1279px) and (min-resolution: 192dpi) 50vw,
    (max-width: 1279px) and (min-resolution: 96dpi) 100vw,
    (min-width: 1280px) and (min-resolution: 288dpi) 300px,
    (min-width: 1280px) and (min-resolution: 192dpi) 600px,
    (min-width: 1280px) and (min-resolution: 96dpi) 1200px,
    100vw`;
    }

    if (this.layout === 'two-thirds') {
      return `
      (max-width: 767px) and (min-resolution: 288dpi) 33vw,
      (max-width: 767px) and (min-resolution: 192dpi) 50vw,
      (max-width: 767px) and (min-resolution: 96dpi) 100vw,
      (min-width: 768px) and (max-width: 1279px) and (min-resolution: 288dpi) 33vw,
      (min-width: 768px) and (max-width: 1279px) and (min-resolution: 192dpi) 33vw,
      (min-width: 768px) and (max-width: 1279px) and (min-resolution: 96dpi) 66vw,
      (min-width: 1280px) and (min-resolution: 288dpi) 400px,
      (min-width: 1280px) and (min-resolution: 192dpi) 400px,
      (min-width: 1280px) and (min-resolution: 96dpi) 800px,
      100vw`;
    }
  }

  private async openOverlay() {
    await this.overlay.openOverlay();
  }

  private renderArticlesContainer() {
    return (
      <div class={'articles-container'}>
        <div class={'articles-container-inner'}>{this.mapArticles()}</div>
      </div>
    );
  }

  private mapArticles() {
    return this.articles.map((article: Product, index) => {
      const key = `article-${index}`;
      return (
        <cms-product-card
          key={key}
          article={article}
          ctaLabel={article.ctaText}
          layout='horizontal'
          ctaStyle='button'
          priceSize='sm'
          ratingTitle={this.ratingTitle}
          datasheetAccessibleName={this.datasheetAccessibleName}
        />
      );
    });
  }

  private renderShowroomImg() {
    return (
      <div
        onClick={() => {
          this.openOverlay();
        }}
        onKeyDown={(event) => {
          if (isEnterKey(event)) {
            this.openOverlay();
          }
        }}
        tabIndex={-1}
        hb-data-namespace={`${this.namespace}-open`}
        class='showroom-img-wrapper'
      >
        {this.responsiveImages != null
          ? renderResponsiveImages(
              this.responsiveImages,
              this.responsiveImagesSizes(),
              this.imageAltText,
              'showroom-images',
            )
          : ''}

        <button type='button' class='open-overlay-wrapper'>
          <span class='open-overlay-text'>
            {this.openOverlayText}
            <span class='open-overlay-icon' aria-hidden={true}>
              +
            </span>
          </span>
        </button>
      </div>
    );
  }

  private getCssClassMap(): CssClassMap {
    return {
      'showroom-container-inner': true,
      [this.layout]: true,
    };
  }

  connectedCallback() {
    if (this.images) {
      this.objectImagesWatcher(this.images);
    }

    if (this.products) {
      this.arrayProductsWatcher(this.products);
    }
  }

  componentWillLoad() {
    const showroomStaticContainer: NodeList = document.querySelectorAll(
      '.cms-showroom-section',
    );
    if (showroomStaticContainer !== undefined) {
      for (const staticElement of Array.from(showroomStaticContainer)) {
        (staticElement as HTMLElement).style.minHeight = 'auto';
      }
    }
  }

  componentDidLoad() {
    this.overlay = this.hostElement.shadowRoot.querySelector(
      'cms-overlay',
    ) as unknown as CmsOverlay;
  }

  render() {
    return (
      <Host>
        <div class='showroom-container'>
          <div class={this.getCssClassMap()}>
            {this.renderShowroomImg()}
            <cms-overlay
              open={this.open}
              headline={this.overlayHeadline}
              imageAltText={this.imageAltText}
              responsiveImages={this.responsiveImages}
              contentSlot='content'
              namespace={this.namespace}
            >
              <div slot='content'>{this.renderArticlesContainer()}</div>
            </cms-overlay>
          </div>
        </div>
      </Host>
    );
  }
}
